import { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import _, { debounce } from 'lodash';
import { init, EChartsOption, ECharts } from 'echarts';
import { getColor } from 'app/utils';
import { LeadNowAbos, LeadNowType, LeadNowVM } from 'app/stores/ui/agt.leadnow.ui.store';
import { useStore } from 'app/context';

interface ILeadNowBarChart {
	type: LeadNowType;
	items: LeadNowVM[];
	abo?: string;
}

// see https://echarts.apache.org/handbook/en/concepts/dataset/
export interface IGraphDataset {
	dimensions: string[];
	source: {
		[key: string]: number | string;
	};
}

export const LeadNowBarChart = observer((props: ILeadNowBarChart) => {
	const type = props.type === LeadNowType.brutto ? 'brutto' : 'netto';
	const { uiStore } = useStore();

	let kwData = props.items;
	if (!kwData) return null;

	// echarts stuff
	const chartRef = useRef<HTMLDivElement>(null);

	const leadData = useMemo(() => {
		// clone LeadNowAbos
		let data = JSON.parse(JSON.stringify(props.abo ? LeadNowAbos.filter((a) => a.path === props.abo) : LeadNowAbos));
		data.forEach((ld: any) => {
			kwData.forEach((kw) => {
				ld.val = ld.val + kw[ld.path][type];
			});
		});
		return data.filter((ld: any) => ld.val > 0);
	}, [kwData, type, props.abo]);

	useEffect(() => {
		const dim = ['path', 'label', 'val'];

		// const legendData = leadData.map((ld: any) => ({ name: ld.label }));

		const chartHeight = 350;
		const chartFontSize = uiStore.printMode ? 12 : 14;

		let option: EChartsOption = {
			color: getColor('all'),
			dataset: {
				dimensions: dim,
				source: leadData,
			},
			yAxis: {
				type: 'category',
				axisLabel: {
					interval: 0,
					fontSize: chartFontSize,
					padding: [0, 5, 0, 0],
				},
			},
			xAxis: {
				type: 'value',
			},
			series: {
				type: 'bar',
				encode: {
					y: 'label',
					x: 'val',
				},
				colorBy: 'data',
			},
			legend: {
				show: true,
			},
			grid: {
				containLabel: true,
			},
			animation: !uiStore.printMode,
		};

		let chart: ECharts | null = null;
		if (chartRef.current !== null) {
			chart = init(chartRef.current, null, { renderer: 'svg', height: chartHeight });
			chart.setOption(option);
		}
		const handleResize = debounce(() => {
			chart?.resize();
		}, 300);
		window.addEventListener('resize', handleResize);

		return () => {
			chart?.dispose();
			window.removeEventListener('resize', handleResize.cancel);
		};
	}, [chartRef, leadData, uiStore.printMode]);

	if (leadData.length <= 1) {
		return null;
	}

	return (
		<>
			<div style={{ width: '100%', height: 'auto', background: '#ffffff' }} ref={chartRef}></div>
		</>
	);
});
